import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { makeStyles } from '@mui/styles';
import React from 'react';

import { DataAPIContext } from '../api';
import { UPDATE } from '../api/dataProvider';
import SandboxedHtmlPreview from '../components/SandboxedHtmlPreview';

const useStyles = makeStyles((theme: any) => ({
    // Plain-text rendered output (e.g. email subject, chat body). Same
    // box styling as the HTML preview but no monospace — recipients see
    // these in a normal font, so the preview should match.
    plainPane: {
        background: 'white',
        border: '1px solid #ddd',
        padding: theme.spacing(2),
        maxHeight: 320,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    fieldLabel: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
}));

// Render ``email_message`` as HTML; everything else as plain text so any
// template tags accidentally left in get visible.
const HTML_FIELDS = new Set(['email_message']);

// Map the backend's per-method field keys to operator-friendly labels.
// Anything not in this table falls back to the raw key, so missing
// entries are visible (rather than silently swallowed) when a new
// per-method field gets added.
const FIELD_LABELS: Record<string, string> = {
    email_subject: 'Subject',
    email_message: 'Message',
    email_message_text: 'Message (plain text)',
    sms_message: 'SMS',
    whatsapp_message: 'WhatsApp',
    chat_message: 'Chat',
    airbnb_message: 'Airbnb',
    push_title: 'Push title',
    push_body: 'Push body',
};

interface Anchor {
    gtu_id?: number | null;
    user_id?: number | null;
    group_tour_id?: number | null;
    group_tour_chat_channel_id?: number | null;
    label?: string;
}

interface PreviewResponse {
    rendered: Record<string, string>;
    method: number;
    anchor?: Anchor;
    // Referenced {{ variables }} that rendered empty for the sample row —
    // resolved server-side through the real render pipeline, so it reflects
    // exactly what the anchor recipient/channel would see.
    blank_tokens?: string[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaignId: number;
    deliveryMethodId: number;
    methodLabel: string;
}

const CampaignDeliveryMethodPreviewDialog: React.FC<Props> = ({
    open,
    onClose,
    campaignId,
    deliveryMethodId,
    methodLabel,
}) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext) as any;

    const [gtuOverride, setGtuOverride] = React.useState<string>('');
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<PreviewResponse | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            setResult(null);
            setError(null);
            setGtuOverride('');
        }
    }, [open]);

    const onRender = async () => {
        setError(null);
        setResult(null);

        const payload: Record<string, any> = { dm_id: deliveryMethodId };
        const trimmed = gtuOverride.trim();
        if (trimmed) {
            const parsed = Number(trimmed);
            if (!Number.isFinite(parsed) || parsed <= 0) {
                setError('GTU id must be a positive integer (or blank to use first audience row)');
                return;
            }
            payload.anchor = { gtu: parsed };
        }

        setLoading(true);
        try {
            const res = await dataAPI(UPDATE, 'campaign', {
                id: campaignId,
                urlSuffix: 'preview_delivery_method/',
                data: payload,
            });
            const body = res?.data || {};
            if (!body || typeof body.rendered !== 'object') {
                setError('Unexpected response from preview endpoint');
            } else {
                setResult(body as PreviewResponse);
            }
        } catch (e: any) {
            setError(e?.body?.message || e?.message || 'Preview request failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Preview rendered message — {methodLabel}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Renders this delivery method's template fields against a sample row from this
                        campaign's audience. Nothing is sent.
                    </Typography>
                    <TextField
                        label="Anchor GTU id (optional)"
                        helperText="Leave blank to use the first row of the campaign's audience."
                        value={gtuOverride}
                        onChange={(e) => setGtuOverride(e.target.value)}
                        size="small"
                        variant="filled"
                    />
                    <Box>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={onRender}
                            disabled={loading}
                        >
                            {loading ? 'Rendering…' : 'Render preview'}
                        </Button>
                    </Box>
                    {error && <Alert severity="error">{error}</Alert>}
                    {result?.anchor?.label && (
                        <Alert severity="info">
                            Rendered against: <strong>{result.anchor.label}</strong>
                            {result.anchor.gtu_id != null ? ` (GTU #${result.anchor.gtu_id})` : ''}
                        </Alert>
                    )}
                    {result && (result.blank_tokens?.length ?? 0) > 0 && (
                        <Alert severity="warning">
                            These variables rendered blank for this sample:{' '}
                            <strong>
                                {(result.blank_tokens || []).map((t) => `{{ ${t} }}`).join(', ')}
                            </strong>
                            . Other audience rows may fill them in — try another anchor, or remove
                            the variable if it can never apply to this send mode.
                        </Alert>
                    )}
                    {result &&
                        Object.entries(result.rendered).map(([field, value]) => (
                            <Box key={field}>
                                <Typography className={classes.fieldLabel}>
                                    {FIELD_LABELS[field] || field}
                                </Typography>
                                {HTML_FIELDS.has(field) ? (
                                    <SandboxedHtmlPreview
                                        html={value || ''}
                                        title={`Rendered ${FIELD_LABELS[field] || field} preview`}
                                        style={{ height: 320 }}
                                    />
                                ) : (
                                    <Box className={classes.plainPane}>{value || '(empty)'}</Box>
                                )}
                            </Box>
                        ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CampaignDeliveryMethodPreviewDialog;
